import styled, { css, down } from "@xstyled/styled-components";
import { useMemo } from "react";

import { CancelButton, PortalLink, UpdateCardDetailsLink } from "./actions";

import { BillingInformationQuery, Feature } from "@hire/schema";
import { Button, Card, List, Spacing, Text } from "@otta/design";
import { Loading } from "@otta/shared-components";
import { Link } from "@hire/components/links";
import { modularScale, palette, pxToRem } from "@otta/design-tokens";
import { currencyPrefix } from "@hire/util/currencies";
import { useAuthorization } from "@hire/providers/authorization";
import { useSalesOrRenewalLink } from "@hire/hooks/useSalesOrRenewalLink";
import { useUserCompanySubscription } from "@hire/util/hooks";
import { Experiment } from "@hire/constants/experiments";
import { useExperiment } from "@otta/experiments/client";

const LeftCard = styled(Card)`
  text-align: left;
`;

const UpgradeCard = styled(LeftCard)`
  flex-grow: 1;
`;

const LinksCard = styled(LeftCard)`
  margin-bottom: auto;
`;

const EnquireButton = styled(Button)`
  text-decoration: none;
`;

const Row = styled.div<{ gap?: string }>`
  gap: ${p => p.gap ?? "1rem"};
  display: flex;

  ${down(
    "tablet",
    css`
      flex-direction: column;
    `
  )}
`;

const SmallCard = styled.div`
  border: ${pxToRem(1)} solid ${palette.grayscale.shade400};
  max-width: ${pxToRem(200)};
  ${down(
    "tablet",
    css`
      max-width: none;
    `
  )}
  border-radius: 4;
  padding: 1rem;
  flex: 1 1 0;
`;

function ProBenefits({
  isCurrentPlan = false,
}: {
  isCurrentPlan?: boolean;
}): React.ReactElement {
  const { granted: sourcingUpsell } = useAuthorization(Feature.SourcingUpsell);
  const shouldShowSourcingItem = useMemo(
    () => !isCurrentPlan || (isCurrentPlan && !sourcingUpsell),
    [isCurrentPlan, sourcingUpsell]
  );

  return (
    <List type="positive">
      <li>Unlimited number of live jobs</li>
      {shouldShowSourcingItem && (
        <li>Sourcing functionality to outbound message relevant candidates</li>
      )}
      <li>Boosted jobs, shown to candidates first</li>
      <li>Insights, benchmarks and advanced reporting</li>
      <li>Dedicated support from our expert team</li>
    </List>
  );
}

function BookADemo(): React.ReactElement {
  const buttonLink = useSalesOrRenewalLink();

  return (
    <UpgradeCard>
      <Spacing size={0}>
        <Text as={"h3"} size={1} bold>
          Upgrade to Pro
        </Text>
        <ProBenefits />
        <EnquireButton as={Link} level="primary" to={buttonLink} newTab>
          Book a demo
        </EnquireButton>
      </Spacing>
    </UpgradeCard>
  );
}

function ProPage(): React.ReactElement {
  return (
    <LeftCard>
      <Spacing size={0}>
        <Text as={"h3"} size={1} bold>
          Current plan - Pro
        </Text>
        <ProBenefits isCurrentPlan />
      </Spacing>
    </LeftCard>
  );
}

function BasicPage({ name }: { name: string }): React.ReactElement {
  return (
    <Spacing size={0}>
      <LeftCard>
        <Text as={"h3"} size={1} bold>
          Current plan - {name}
        </Text>
      </LeftCard>
      <BookADemo />
    </Spacing>
  );
}

type User = NonNullable<BillingInformationQuery["currentUser"]>;
type Company = NonNullable<User["currentCompany"]>;

type CoreProps = {
  subscription?: NonNullable<Company["activeSubscriptions"]>[0];
  jobSlots: NonNullable<Company["jobSlotData"]>;
};

function Core({ subscription, jobSlots }: CoreProps): React.ReactElement {
  const price = parseFloat(subscription?.price.net ?? "0").toFixed(2);
  const { variant } = useExperiment(Experiment.DisableStartupEssential);

  const isStartupEssentialAvailable = variant !== "variant";
  const showStartupEssentialInfo = isStartupEssentialAvailable || subscription;
  const isCurrentStartupEssentialCustomer = !!subscription;
  const buttonLink = useSalesOrRenewalLink();

  return (
    <Spacing size={1}>
      <LeftCard>
        <Spacing size={0}>
          <Text as={"h3"} size={1} bold>
            Current plan - Startup Essential
          </Text>
          {showStartupEssentialInfo ? (
            <Row>
              <SmallCard>
                <Spacing size={-1}>
                  <Text>Potential live jobs</Text>
                  <Text bold>{jobSlots.total}</Text>
                </Spacing>
              </SmallCard>
              {!!subscription && (
                <SmallCard>
                  <Spacing size={-1}>
                    <Text>Price per month</Text>
                    <Text bold>
                      {currencyPrefix(subscription.price.currency)}
                      {price}
                    </Text>
                  </Spacing>
                </SmallCard>
              )}
            </Row>
          ) : (
            <EnquireButton as={Link} level="primary" to={buttonLink} newTab>
              Book a call
            </EnquireButton>
          )}
          <Row>
            {isCurrentStartupEssentialCustomer ? (
              <CancelButton cancelAt={subscription.cancelAt} />
            ) : isStartupEssentialAvailable ? (
              <EnquireButton
                as={Link}
                level="secondary"
                to="/jobs/payg-pricing"
                data-analytics-id="purchase-payg-cta"
              >
                Purchase Startup Essential
              </EnquireButton>
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <></>
            )}
          </Row>
        </Spacing>
      </LeftCard>
      <Row gap={modularScale(1)}>
        <BookADemo />
        {isCurrentStartupEssentialCustomer && (
          <LinksCard>
            <Spacing size={0}>
              <PortalLink>Manage subscription</PortalLink>
              <PortalLink>View invoices</PortalLink>
              <UpdateCardDetailsLink />
              <PortalLink>View or change billing information</PortalLink>
            </Spacing>
          </LinksCard>
        )}
      </Row>
    </Spacing>
  );
}

export function Billing(): React.ReactElement {
  const {
    loading,
    pro,
    coreFree,
    corePaid,
    hasPaidSlots,
    subscription,
    jobSlots,
  } = useUserCompanySubscription();

  if (loading) {
    return <Loading />;
  }

  return (
    <Spacing size={1}>
      <Text as="h2" align="left" bold size={2}>
        Billing
      </Text>
      {pro ? (
        <ProPage />
      ) : coreFree && jobSlots ? (
        <Core jobSlots={jobSlots} />
      ) : corePaid && jobSlots && subscription ? (
        <Core jobSlots={jobSlots} subscription={subscription} />
      ) : (
        <BasicPage name={hasPaidSlots ? "Essential" : "Basic"} />
      )}
    </Spacing>
  );
}
